import { useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAccounts } from '../../context/AccountContext';
import { useTranslation } from "react-i18next";
import { useGenerateTemporaryAccessPass } from '../../hooks/MfaHooks';
import { LockStatusResult } from '../../types/models';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { HelpMeAlert } from '../HelpMeAlert';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { Card } from '@uhg-abyss/web/ui/Card';
import { styled } from '@uhg-abyss/web/tools/styled';
import SelectedAccountInfoBox from '../SelectedAccountInfoBox';
import SwitchAccountButton from '../SwitchAccountButton';

const CardBlock = styled('div', { padding: '15px', margin: '4px' });
const HeadingBlock = styled('div', { padding: '1rem', margin: '0.25rem' });
const DescriptionBlock = styled(Text, { display: 'block', padding: '1rem', margin: '0.25rem' });
const AccountSwitcherLink = styled(SwitchAccountButton, { fontSize: '1rem' });
const BoldLink = styled(Link, { fontWeight: '$web.core.font-weight.enterprise-sans-vf.bold' });

const MFA_REGISTRATION_URL = 'https://aka.ms/mfasetup';

// Not localized - mirrors VerifiedId.tsx's formatCountdown, a plain code-level formatter.
const formatLifetime = (minutes: number): string => {
    if (minutes > 0 && minutes % 60 === 0) {
        const hours = minutes / 60;
        return hours === 1 ? '1 hour' : `${hours} hours`;
    }
    return minutes === 1 ? '1 minute' : `${minutes} minutes`;
};

export const SetupMfa = () => {
    const { tapEnabled } = useAuth();
    const { lockStatus, selectedAccount, accounts } = useAccounts();
    const { t } = useTranslation();

    const { mutate, data, isPending: isLoading, isError } = useGenerateTemporaryAccessPass();

    const isSelectedAccountTapEnabled = selectedAccount?.isTapEnabled ?? false;

    // Don't request a TAP until the unlock check has resolved and the selected account is
    // TAP-eligible - matches the "cannot start MFA setup if account is locked" requirement,
    // and re-fires if the employee switches to a different (eligible) account on this page.
    useEffect(() => {
        if (
            (lockStatus === LockStatusResult.NoAction || lockStatus === LockStatusResult.Unlocked)
            && selectedAccount
            && isSelectedAccountTapEnabled
        ) {
            mutate({ resourceId: selectedAccount.resourceId, userId: selectedAccount.userId });
        }
    }, [lockStatus, selectedAccount, isSelectedAccountTapEnabled, mutate]);

    // Home's link into this page is disabled until the lock check resolves, so reaching
    // this point while still Pending only happens via direct URL navigation - send them back.
    if (
        !tapEnabled
        || lockStatus === LockStatusResult.Pending
        || lockStatus === LockStatusResult.Error
    ) {
        return <Navigate to="/home" replace />;
    }

    const tapResult = data?.data;
    const isFailure = isError
        || (tapResult != null && tapResult.Response !== 'success');
    const displayFailure = isSelectedAccountTapEnabled && isFailure;
    const displayLoadingSpinner = isSelectedAccountTapEnabled && isLoading;
    const displayTapInfo = isSelectedAccountTapEnabled
        && !isLoading
        && !isFailure
        && tapResult;

    // "AccountNotSynced" is the one failure reason with a distinct, actionable message -
    // everything else (including a network/mutation-level failure with no parsed result at all)
    // falls back to the generic retry/help-desk message.
    const errorMessageKey = tapResult?.ErrorCode === 'AccountNotSynced'
        ? 'setupMfaErrorAccountNotSynced'
        : 'setupMfaErrorGeneric';

    // Best-effort clipboard copy - navigation to the registration link proceeds regardless.
    const handleLinkClick = () => {
        if (tapResult?.TemporaryAccessPass) {
            navigator.clipboard?.writeText(tapResult.TemporaryAccessPass).catch(() => {});
        }
    };

    const registrationLink = () => {
        // Only offer "Switch Account" when there's actually another account to switch to.
        return accounts.length > 1
            ? (<>
                <BoldLink to="/home">{t('goHome')}</BoldLink> {t('or')} <AccountSwitcherLink hideIcon id="switchAccountLink" />
            </>) : (
                <BoldLink to="/home">{t('goHome')}</BoldLink>
            );
    };


    return (
        <Layout.Stack alignLayout="center">
            <HeadingBlock>
                <Heading level={3} color="$textPrimary" id="setupMfaHeading" textAlign="center">
                    {t('setupMfaHeading')}
                </Heading>
            </HeadingBlock>
            <DescriptionBlock color="$textPrimary" data-testid="setupMfaDescription">
                {t('setupMfaDescription')}
            </DescriptionBlock>

            <SelectedAccountInfoBox />

            {!isSelectedAccountTapEnabled && (
                <HelpMeAlert status="info" data-testid="tapNotAvailableMsg" title={t('noTapOnAccountTitle')}>
                    {t('tapNotAvailableMsg')}
                    <br />
                    {registrationLink()}
                </HelpMeAlert>
            )}

            {displayFailure && (
                <HelpMeAlert status="error" title={t('setupMfaError1')}>
                    {t(errorMessageKey)}
                </HelpMeAlert>
            )}

            {displayLoadingSpinner && (
                <LoadingSpinner isLoading ariaLoadingLabel={t('setupMfaLoadingMsg')} />
            )}

            {displayTapInfo && (
                <CardBlock>
                    <Card>
                        <Layout.Stack alignLayout="center">
                            <Text color="$textPrimary" fontWeight="bold" data-testid="setupMfaTapTitle">
                                {t('setupMfaTapTitle')}
                            </Text>
                            <Text color="$textPrimary" data-testid="setupMfaTapDescription">
                                {t('setupMfaTapDescription', { lifetime: formatLifetime(tapResult.LifetimeInMinutes) })}
                            </Text>
                            <Text color="$textPrimary" fontWeight="bold" data-testid="setupMfaTap">
                                {tapResult.TemporaryAccessPass}
                            </Text>
                            <Text color="$textPrimary" fontWeight="bold" data-testid="setupMfaCopyInstructions">
                                {t('setupMfaCopyInstructions')}
                            </Text>
                            <a
                                href={MFA_REGISTRATION_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="setupMfaLink"
                                onClick={handleLinkClick}
                            >
                                {t('setupMfaLinkText')}
                            </a>
                        </Layout.Stack>
                    </Card>
                </CardBlock>
            )}
        </Layout.Stack>
    );
};
